import { motion } from "framer-motion";
import {
  Heart,
  MoreVertical,
  Pause,
  Play,
  Repeat,
  Share2,
  Shuffle,
  SkipBack,
  SkipForward,
  Vibrate,
  VibrateOff,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import { PlayerMenu } from "./PlayerMenu";
import { PlayerVolume } from "./PlayerVolume";

interface PlayerControlsProps {
  isPlaying: boolean;
  isLiked: boolean;
  loopMode: "off" | "one" | "all";
  volume: number;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onLoopChange: (mode: "off" | "one" | "all") => void;
  onVolumeChange: (value: number) => void;
  onLikeToggle: () => void;
  onShare: () => void;
  mini?: boolean;
  isHapticsSupported: boolean;
  isHapticsEnabled: boolean;
  onHapticsToggle: () => void;
  onAddToPlaylist: () => void;
  onAddToAlbum: () => void;
}

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  isPlaying,
  isLiked,
  loopMode,
  volume,
  isHapticsSupported,
  isHapticsEnabled,
  onPlayPause,
  onNext,
  onPrevious,
  onLoopChange,
  onVolumeChange,
  onHapticsToggle,
  onLikeToggle,
  onShare,
  mini = false,
  onAddToPlaylist,
  onAddToAlbum,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

  // Long press and gesture handling
  const handleLongPress = useCallback((action: "next" | "previous") => {
    const timer = setTimeout(() => {
      // Implement fast forward/rewind logic
      console.log(`Long press ${action}`);
    }, 500);
    setLongPressTimer(timer);
  }, []);

  const handleTouchEnd = useCallback(
    (action: "next" | "previous") => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      // Perform standard skip
      action === "next" ? onNext() : onPrevious();
    },
    [onNext, onPrevious, longPressTimer]
  );

  return (
    <div
      className={`flex items-center justify-between w-full ${
        mini ? "max-w-md" : "max-w-full"
      } mx-auto md:px-2 [&>div]:w-1/3 touch-target`}
      // Prevent default touch behaviors
      // onTouchStart={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-2 sm:gap-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className={`p-2 sm:p-3 touch-target ${
            isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"
          } transition`}
          onClick={onLikeToggle}
          aria-label="Like/Unlike track"
        >
          <Heart className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} />
        </motion.button>

        {!mini && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="hidden md:flex p-2 sm:p-3 touch-target text-zinc-400 hover:text-white transition"
            onClick={() => {}} // Add shuffle functionality
            aria-label="Shuffle tracks"
          >
            <Shuffle className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
        )}

        {isHapticsSupported && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onHapticsToggle}
            className={`flex p-2 touch-target rounded-full transition ${
              isHapticsEnabled ? "text-yellow-500" : "text-zinc-400 hover:text-white"
            }`}
            aria-label="Toggle haptic feedback"
          >
            {isHapticsEnabled ? <Vibrate className="w-6 h-6" /> : <VibrateOff className="w-6 h-6" />}
          </motion.button>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 sm:gap-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onPrevious}
          onTouchStart={() => handleLongPress("previous")}
          onTouchEnd={() => handleTouchEnd("previous")}
          className="p-2 sm:p-3 touch-target text-zinc-400 hover:text-white transition"
          aria-label="Previous track"
        >
          <SkipBack className="w-6 h-6 sm:w-7 sm:h-7" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onPlayPause}
          className="p-3 sm:p-4 touch-target bg-white rounded-full hover:scale-105 transition"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <Pause className="w-6 h-6 sm:w-7 sm:h-7 text-black fill-black" />
          ) : (
            <Play className="w-6 h-6 sm:w-7 sm:h-7 text-black fill-black" />
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onNext}
          onTouchStart={() => handleLongPress("next")}
          onTouchEnd={() => handleTouchEnd("next")}
          className="p-2 sm:p-3 touch-target text-zinc-400 hover:text-white transition"
          aria-label="Next track"
        >
          <SkipForward className="w-6 h-6 sm:w-7 sm:h-7" />
        </motion.button>
      </div>

      <div className="flex items-center justify-end gap-2 sm:gap-4">
        {!mini && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onLoopChange(loopMode === "off" ? "all" : loopMode === "all" ? "one" : "off")}
            className={`p-2 sm:p-3 touch-target transition relative ${
              loopMode !== "off" ? "text-green-500" : "text-zinc-400 hover:text-white"
            }`}
            aria-label="Change loop mode"
          >
            <Repeat className="w-4 h-4 sm:w-5 sm:h-5" />
            {loopMode === "one" && (
              <span className="absolute top-[.6rem] md:top-[.9rem] right-[.8rem] md:right-[1.2rem] text-[8px] sm:text-[10px] font-bold">
                1
              </span>
            )}
          </motion.button>
        )}

        <PlayerVolume volume={volume} onVolumeChange={onVolumeChange} />

        {!mini && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onShare}
            className="hidden md:flex p-2 sm:p-3 touch-target text-zinc-400 hover:text-white transition"
            aria-label="Share track"
          >
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
        )}

        <PlayerMenu
          isLiked={isLiked}
          isHapticsSupported={isHapticsSupported}
          isHapticsEnabled={isHapticsEnabled}
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          onLikeToggle={onLikeToggle}
          onShare={onShare}
          onAddToPlaylist={onAddToPlaylist}
          onAddToAlbum={onAddToAlbum}
          trigger={
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 sm:p-3 touch-target text-zinc-400 hover:text-white transition"
              aria-label="Open player menu"
            >
              <MoreVertical className="w-6 h-6" />
            </motion.button>
          }
        />
      </div>
    </div>
  );
};
